import { BaseModal } from "@/components/modal/base-modal";
import { FilledButton, OutlinedButton } from "@/components/ui/buttons";
import { useSubmitMealFeedback } from "@/hooks/requests/meal/use-submit-meal-feedback";
import { useToast } from "@/hooks/ui/use-toast";
import { useViewportHeight } from "@/hooks/utils/use-viewport-height";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, useCallback, useMemo, useState } from "react";

interface MealFeedbackSectionProps {
  mealId?: string | null;
}

const FEEDBACK_OPTIONS = [
  { label: "Calories were not right", value: "cal" },
  { label: "Image was not correct", value: "img" },
  { label: "Incorrect portion size", value: "portion" },
  { label: "Didn’t taste nice", value: "taste" },
  { label: "Something else...", value: "other" },
] as const;

const SHEET_HEIGHT_PX = 406;

export const MealFeedbackSection = memo(function MealFeedbackSection({
  mealId,
}: MealFeedbackSectionProps) {
  const canSubmit = Boolean(mealId);
  const viewportHeight = useViewportHeight();
  const { showError, showSuccess } = useToast();
  const { submitMealFeedbackAsync, isSubmitting } = useSubmitMealFeedback();
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const initialBreakpoint = useMemo(() => {
    if (!viewportHeight || viewportHeight <= 0) return 0.7;
    return Math.min(0.9, Math.max(0.45, SHEET_HEIGHT_PX / viewportHeight));
  }, [viewportHeight]);

  const closeSheet = useCallback(() => {
    setIsSheetOpen(false);
  }, []);

  const openSheet = useCallback(() => {
    if (!canSubmit) return;
    setIsSheetOpen(true);
  }, [canSubmit]);

  const handleSelectReason = useCallback(
    async (reason: string) => {
      if (!mealId || isSubmitting) return;

      try {
        await submitMealFeedbackAsync({
          meal_id: mealId,
          reasons: [reason],
        });
        showSuccess("Thanks for your feedback.");
        closeSheet();
      } catch (error) {
        if (error instanceof Error) {
          showError(error.message);
          return;
        }
        showError("Failed to submit feedback. Please try again.");
      }
    },
    [closeSheet, isSubmitting, mealId, showError, showSuccess, submitMealFeedbackAsync],
  );

  return (
    <>
      <div className="flex h-14 items-center justify-center">
        <button
          type="button"
          onClick={openSheet}
          disabled={!canSubmit}
          className={cn(
            textVariants.bodySingleLine,
            "text-os-brand underline underline-offset-2 transition-opacity font-size[13px]",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          Something wrong with this recipe?
        </button>
      </div>

      <BaseModal
        isOpen={isSheetOpen}
        onDismiss={closeSheet}
        backdropDismiss={true}
        initialBreakpoint={initialBreakpoint}
        breakpoints={[0, initialBreakpoint, 0.9]}
      >
        <div className="safe-area-bottom flex flex-col px-9 pt-9 pb-8">
          <h2 className={cn(textVariants.title3, "mb-3 h-8.5 text-center")}>
            Something wrong with this recipe?
          </h2>

          <div className="flex flex-col gap-2">
            {FEEDBACK_OPTIONS.map((option) => (
              <OutlinedButton
                key={option.value}
                type="button"
                color="black"
                textCase="normal"
                onClick={() => void handleSelectReason(option.value)}
                disabled={isSubmitting || !canSubmit}
                className={cn(textVariants.bodySingleLine, "h-11.5")}
              >
                {option.label}
              </OutlinedButton>
            ))}

            <FilledButton
              type="button"
              color="light-gray"
              textCase="normal"
              onClick={closeSheet}
              className={cn(textVariants.bodySingleLine, "h-11.5")}
            >
              Cancel
            </FilledButton>
          </div>
        </div>
      </BaseModal>
    </>
  );
});
